import * as React from "react";
import { IStackStyles, Link, Shimmer, Stack, Text } from "@fluentui/react";
import { MetaItemsGroupsLayoutContext } from "./MetaItemGroupsLayout";
import { buildLabelMetaItemTestIds } from "./MetaItemTypes";

export interface LabelMetaItemAction {
  /**
   * Unique identifier
   */
  id?: string;
  /**
   * Text to be displayed
   */
  label: string;
  /**
   * Function to be executed when the element is clicked
   */
  onClick: () => void;
  /**
   * id used to get the action link
   */
  testId?: string;
  /**
   * Option to enable or disable the action
  */
  disabled?: boolean;
}

export interface LabelMetaItemProps {
  /**
   * Label for the LabelMetaItem
   */
  label: string;
  /**
   * Option to display on the LabelMetaItem
   */
  children: string | JSX.Element;
  /**
   * Callback when clicked on the LabelMetaItem
   */
  action?: LabelMetaItemAction;
  /**
   * Flag to indicate if separator needs to be shown
   */
  hideSeparator?: boolean;
  /**
   * Shows a shimmer if the value is still loading
   */
  loading?: boolean;
  /**
   * Id used to get the label meta item elements for unit tests
   */
  testId?: string;
}

const labelMetaItemStackStyle: IStackStyles = { root: { border: "1px" } };

/**
 * The LabelMetaItem renders a text or component with an option to display as a
 * hyperlink
*/

export const LabelMetaItem = ({
  label,
  children,
  action,
  hideSeparator = false,
  loading,
  testId,
}: LabelMetaItemProps): JSX.Element => {
  const { labelWidth } = React.useContext<{ labelWidth: number }>(MetaItemsGroupsLayoutContext);
  const actionLink = action
    ? (
      <Text id={action.id}>
        (
        <Link data-test-id={action.testId} onClick={action.onClick} disabled={action.disabled}>{action.label}</Link>
        )
      </Text>
    )
    : undefined;

  const separatorMargin = 10;
  const separatorWidth = 2 * separatorMargin + 4;
  const itemLabelWidth = labelWidth || 120;
  const itemLabelCssWidth = `${itemLabelWidth}px`;
  const itemValueCssWidth = `calc(100% - ${itemLabelWidth + (hideSeparator ? 0 : separatorWidth)}px)`;

  return (
    <Stack
      data-test-id={buildLabelMetaItemTestIds(testId).component}
      horizontal
      styles={labelMetaItemStackStyle}
    >
      <span
        style={{
          width: itemLabelCssWidth,
          minWidth: itemLabelCssWidth,
          wordBreak: "break-word",
        }}
      >
        <Text data-test-id={buildLabelMetaItemTestIds(testId).label}>{label}</Text>
        {" "}
        {actionLink}
      </span>
      <span style={{ textAlign: "center", margin: `0px ${separatorMargin}px` }}>
        <Text>{!hideSeparator ? ":" : ""}</Text>
      </span>
      <span
        style={{
          width: itemValueCssWidth,
          minWidth: itemValueCssWidth,
          wordBreak: "break-word",
        }}
      >
        <Shimmer
          styles={{ dataWrapper: { position: "relative", top: 0 } }}
          width="100%"
          isDataLoaded={loading === undefined ? true : loading}
        >
          <div data-test-id={buildLabelMetaItemTestIds(testId).value}>
            {(typeof children === "string") ? <Text>{children}</Text> : children}
          </div>
        </Shimmer>
      </span>
    </Stack>
  );
};
